import type { ReactNode } from 'react';
import type { ChatMessage } from '../../providers/types';
import { normalizeProducts } from '../../lib/normalizeProducts';
import { ProductCardMessage } from './ProductCardMessage';
import { MediaMessage } from './MediaMessage';
import { VideoMessage } from './VideoMessage';

// Linkify plain text: split on URL pattern and render http(s) URLs as <a> tags.
// Keeps the rest of the text as-is. Intentionally simple — not a full URL parser.
const URL_RE = /(https?:\/\/[^\s<]+)/g;
function linkifyText(text: string, isOwn: boolean): ReactNode[] {
  const parts = text.split(URL_RE);
  return parts.map((part, idx) =>
    URL_RE.test(part) ? (
      <a
        key={idx}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline cursor-pointer break-all ${
          isOwn ? 'text-white hover:text-white/80' : 'text-brand-gold hover:text-brand-gold-dark'
        }`}
      >
        {part}
      </a>
    ) : (
      <span key={idx}>{part}</span>
    )
  );
}

interface RichMessageProps {
  message: ChatMessage;
  isOwn: boolean;
  onProductClick?: (productId: number, variantId?: number) => void;
}

export function RichMessage({ message, isOwn, onProductClick }: RichMessageProps) {
  const { attributes, attachedMedia, body, timestamp } = message;

  const timestampEl = (
    <p className={`text-xs mt-2 ${isOwn ? 'text-white/60' : 'text-brand-navy/60'}`}>
      {timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </p>
  );

  // Priority 1: Product card messages (Variant A or B)
  if (attributes) {
    // Variant A: attributes.type === 'product'
    // Variant B: attributes.shared?.type === 'product'
    const isProductA = attributes.type === 'product';
    const shared = attributes.shared as Record<string, unknown> | undefined;
    const isProductB = shared?.type === 'product';

    if (isProductA || isProductB) {
      const products = normalizeProducts(attributes);
      if (products && products.length > 0) {
        return (
          <div>
            <ProductCardMessage products={products} isOwn={isOwn} onProductClick={onProductClick} />
            {timestampEl}
          </div>
        );
      }
    }

    // Priority 2: Video message
    if (attributes.type === 'video' && typeof attributes.url === 'string') {
      return (
        <div>
          <VideoMessage url={attributes.url} />
          {timestampEl}
        </div>
      );
    }
  }

  // Priority 3: Media message (attachedMedia present)
  if (attachedMedia && attachedMedia.length > 0) {
    return (
      <div>
        <MediaMessage attachedMedia={attachedMedia} />
        {timestampEl}
      </div>
    );
  }

  // Priority 4: Default plain text (current behavior).
  // `overflow-wrap-anywhere` + `break-words` keep URL-only or no-space messages
  // inside the bubble width.
  return (
    <div>
      <p className="break-words [overflow-wrap:anywhere]">
        {body ? linkifyText(body, isOwn) : null}
      </p>
      {timestampEl}
    </div>
  );
}
